import { useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { playNotificationSound } from '../services/notificationSound';

const POLL_INTERVAL = 5000; // 5 seconds

/**
 * Global hook that polls for unread chat messages and plays a notification
 * sound whenever the unread count increases. Runs on every authenticated
 * page so the user hears the sound no matter where they are in the portal.
 */
export default function useChatNotification() {
  const prevCountRef = useRef(null);
  const initialRef = useRef(true);

  const poll = useCallback(async () => {
    try {
      const res = await api.get('/messages/chat/unread-count/');
      const count = res.data.count;

      // On the very first poll, just record the baseline – don't play sound
      if (initialRef.current) {
        initialRef.current = false;
        prevCountRef.current = count;
        return;
      }

      // If unread count went up, a new chat arrived – play sound
      if (prevCountRef.current !== null && count > prevCountRef.current) {
        playNotificationSound();
      }

      prevCountRef.current = count;
    } catch {
      // silently ignore – user might be on a public page or token expired
    }
  }, []);

  useEffect(() => {
    // Reset on mount so component re-mounts start clean
    prevCountRef.current = null;
    initialRef.current = true;

    // Initial poll
    poll();

    // Set up interval
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [poll]);
}
